import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import { purgeStoredState } from 'redux-persist';
import { asyncSessionStorage } from 'redux-persist/storages';
import * as types from '../constants/ActionTypes';
import Page from '../../lib/page';
import { asyncOrigins, chatBotMessageUsRefIdsMapping } from '../../common/commonConstants';
import { initiateAsyncChat, isAsyncUser } from '../../lib/asyncMessageHelper';
import poll from '../../lib/poll';
import { nodeChatOnPageValidator, nodeChatOnWindowValidator } from '../../lib/node-chat-on-page-validator';
import fireImpressionFptiForChatLoadError from '../../lib/fire-impression-fpti-for-chat-load-error';
import { getNodeChatPollValues } from '../../lib/globalHelper';
import { loadChatIneligibleModalContainer } from './action-chat-ineligible-modal';
import { resetAllChatClicksInSession, wasChatBotClicked } from '../../lib/chat-bot-live-helper';
import { isLoggedOutToLogin } from './action-step-up';
import { postChatBotEligibility } from './action-postchatbot-eligibility';
import { closeModalContainer } from './action-login-modal';

const chatBotSessionURL = '/smartchat/chat-meta';

function requestChatBotSessionChecker() {
  return { type: types.REQUEST_CHAT_BOT_SESSION };
}

function receivedChatBotSessionResult() {
  return { type: types.RECEIVE_CHAT_BOT_SESSION_RESULT, payload: undefined };
}

export const catchChatBotSessionError = () => ({ type: types.CATCH_CHAT_BOT_SESSION_ERROR });

export const fetchChatBotSessionChecker = (options) => (dispatch) => {
  dispatch(requestChatBotSessionChecker());
  return axios
    .get(chatBotSessionURL)
    .then((response) => {
      dispatch(receivedChatBotSessionResult());
      if (options && typeof options.callBack === 'function') {
        options.callBack(response.data);
      }
    })
    .catch(() => {
      dispatch(catchChatBotSessionError());
      if (options && typeof options.callBack === 'function') {
        options.callBack(null);
      }
    });
};

export const chatBotClickedAction = () => (dispatch) => dispatch({ type: types.CHATBOT_CLICKED });

export const chatBotClickedFalseAction = () => (dispatch) => dispatch({ type: types.CHATBOT_CLICKED_FALSE });

// ------------------------- POST CHAT BOT ELIGIBILITY LOGIC ------------------------

const isLoggedIn = Page.isLoggedIn();

export const asyncChatResolver = (topicRefId, subject) => {
  setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);
  const asyncOrigin = topicRefId.includes(chatBotMessageUsRefIdsMapping.FINAL_INTENT)
    ? asyncOrigins.FINAL_CONTACT_OPTIONS
    : asyncOrigins.DEFAULT_CONTACT_OPTIONS;
  return initiateAsyncChat({ topicRefId, origin: asyncOrigin, subject });
};

// Call back function for post chat bot eligibility to launch appropriate channel after getting the service response.
const showChatBotWhenPresentOnPage =
  (topic = {}, fromChannel) =>
  (dispatch, getState) => {
    const {
      postChatBotEligibility: { chatBotAsync },
    } = getState();
    const topicRefId =
      topic && topic.topicRefId
        ? `${topic.topicRefId}|${chatBotMessageUsRefIdsMapping.FINAL_INTENT}`
        : chatBotMessageUsRefIdsMapping.GENPOOL_CONTACT_US;

    // subject - it is the topic name and is passed while launching the messaging chatbot
    const subject = topic && topic.topicName;
    const pollValues = getNodeChatPollValues();

    // If async user launch messageUs
    if (isAsyncUser(chatBotAsync)) {
      return asyncChatResolver(topicRefId, subject);
    }

    // If user is non async chatBot will be launched.
    // Waits at least 3.20 secs based on current retries = 5 and delay = 200 ms
    return poll(nodeChatOnPageValidator, pollValues.retries, pollValues.delay)
      .then(() => {
        // eslint-disable-next-line no-undef
        nodeChat.show(fromChannel, topicRefId, subject);
      })
      .catch(() => fireImpressionFptiForChatLoadError());
  };

/**
 * For Opening the chat ineligible modal when post chatbot eligility API returns ineligible
 * @returns {Function}
 */
const openIneligibleModalContainer = () => (dispatch) => {
  const pollValues = getNodeChatPollValues();
  return poll(nodeChatOnWindowValidator, pollValues.retries, pollValues.delay)
    .then(() => null)
    .catch(() => {
      dispatch(loadChatIneligibleModalContainer());
    });
};

// If chatbot is clicked and user is logged out to login then else block will be executed if not if block will be executed.
const performPostChatBotEligibility = (postChatBotResponse) => (dispatch, getState) => {
  const state = getState();
  if ((postChatBotResponse && !isLoggedIn) || (isLoggedIn && !(wasChatBotClicked() && isLoggedOutToLogin(state)))) {
    dispatch(postChatBotEligibility(postChatBotResponse));
  } else {
    resetAllChatClicksInSession();
    dispatch(closeModalContainer());
    dispatch(
      postChatBotEligibility({
        postChatBotResponse,
        showChat: showChatBotWhenPresentOnPage,
        reject: openIneligibleModalContainer,
      }),
    );
  }
};

/**
 * Calling postChatBot Eligibility API for checking the eligibility to show/hide chatBot
 */

export const chatBotEligibilityResponse = () => (dispatch, getState) => {
  const state = getState();
  let topic = null;
  let postChatBotResponse;

  if (isLoggedOutToLogin(getState())) {
    topic = state.modal.topic;
  } else {
    topic = state.selectResult.topicTreeSelectResult;
  }

  // from log out to login after clicking on chatbot in final intent page
  if (!_isEmpty(topic)) {
    postChatBotResponse = Page.getChatBotEligibilityParams(topic.topicRefId, 'TOPIC');
  }

  // from log out to login after clicking on chatbot in default page
  if (_isEmpty(topic)) {
    postChatBotResponse = Page.getChatBotEligibilityParams('contactUSpage', 'GENERIC');
  }

  return dispatch(performPostChatBotEligibility(postChatBotResponse));
};

// ------------------ END OF POST CHAT BOT ELIGIBILITY LOGIC ----------------------------
